import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import type { CurrencyFindManyQueryInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find-many-query.dto';
import type { CurrencyFindInterface } from '@zaoblako/shared/modules/catalog/dtos/currency/currency-find.dto';
import { BasisCurrencyService } from '@zaoblako/web/core/services/basis/basis-currency.service';
import { SingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/single-select/single-select.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { HighlightMatchTextHelper } from '@zaoblako/web/shared/helpers/highlight-match-text.helper';
import { SafeHtmlPipe } from '@zaoblako/web/shared/pipes/safe-html.pipe';

@Component({
  selector: 'crm-currency-single-select',
  standalone: true,
  imports: [CommonModule, SingleSelectComponent, SpinnerComponent, SafeHtmlPipe],
  templateUrl: './currency-single-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CurrencySingleSelectComponent implements OnInit {
  @ViewChild('singleSelectComponent', { static: true }) public singleSelectComponent: SingleSelectComponent;

  @Input() public currencies: CurrencyFindInterface[] = [];
  public queryParams: CurrencyFindManyQueryInterface = {
    limit: 40,
    offset: 0
  };

  public loading = false;
  public paginationLoading = false;
  public count = 0;

  public _selectedCurrency: CurrencyFindInterface;
  @Input() public set selectedCurrency(selectedCurrency: CurrencyFindInterface) {
    if (this.customSearch) {
      this._selectedCurrency = selectedCurrency;
      return;
    }

    if (this.currencies.some((currency) => currency.id === selectedCurrency?.id)) {
      this._selectedCurrency = this.currencies.find((currency) => currency.id === selectedCurrency.id);
      return;
    }

    if (selectedCurrency?.id) {
      this.basisCurrencyService
        .findWithPagination({ includeIds: [selectedCurrency.id], limit: 1, offset: 0 })
        .subscribe({
          next: (data) => {
            if (data.items[0]) {
              const _currency = data.items[0];
              if (!this.currencies.some((currency) => currency.id === _currency.id)) {
                this.currencies = this.currencies.concat(_currency);
                this.cdRef.markForCheck();
              }
              setTimeout(() => {
                this._selectedCurrency = _currency;
                this.cdRef.markForCheck();
              }, 30);
            }
          }
        });
    }
  }

  @Output() public selectedCurrencyChange = new EventEmitter<CurrencyFindInterface>();

  @Input() public isError = false;

  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public disabled: boolean;
  @Input() public appendTo: string;
  @Input() public showClear = true;
  @Input() public labelInput = false;

  @Input() public customSearch = false;

  @Input() public set setSelectedCurrencyId(id: string) {
    if (id === null || id === undefined) {
      return;
    }

    if (this.currencies.some((currency) => currency.id === id)) {
      this._selectedCurrency = this.currencies.find((currency) => currency.id === id);
      return;
    }

    this.basisCurrencyService.findWithPagination({ includeIds: [id], limit: 1, offset: 0 }).subscribe({
      next: (data) => {
        if (data.items[0]) {
          const _currency = data.items[0];
          if (!this.currencies.some((currency) => currency.id === _currency.id)) {
            this.currencies = this.currencies.concat(_currency);
            this.cdRef.markForCheck();
          }
          setTimeout(() => {
            this._selectedCurrency = _currency;
            this.cdRef.markForCheck();
          }, 30);
        }
      }
    });
  }

  public constructor(
    private readonly basisCurrencyService: BasisCurrencyService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  public ngOnInit(): void {
    if (!this.customSearch) {
      this.loading = true;
      this.cdRef.markForCheck();

      this.basisCurrencyService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          this.currencies = this.currencies?.length
            ? this.currencies.concat(
                data.items.filter((item) => !this.currencies.some((currency) => currency.id === item.id))
              )
            : data.items.filter((item) => !this.currencies.some((currency) => currency.id === item.id));
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public onSelect(currency: CurrencyFindInterface) {
    this.selectedCurrencyChange.emit(currency);
  }

  public toCurrency(item: any): CurrencyFindInterface {
    return item as CurrencyFindInterface;
  }

  public onShowPanel() {
    if (!this.customSearch) {
      setTimeout(() => {
        this.singleSelectComponent?.dropdown?.scroller.onScroll.subscribe(() => {
          const el: HTMLElement = this.singleSelectComponent?.dropdown?.scroller?.elementViewChild?.nativeElement;
          if (el.offsetHeight + Math.ceil(el.scrollTop) >= el.scrollHeight) {
            if (this.currencies.length < this.count && !this.loading && !this.paginationLoading) {
              this.paginationLoading = true;
              this.queryParams.offset += this.queryParams.limit;
              this.cdRef.markForCheck();

              this.basisCurrencyService.findWithPagination(this.queryParams).subscribe({
                next: (data) => {
                  this.currencies = this.currencies.concat(
                    data.items.filter((item) => !this.currencies.some((currency) => currency.id === item.id))
                  );
                  this.count = data.count;
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                },
                error: () => {
                  this.paginationLoading = false;
                  this.cdRef.markForCheck();
                }
              });
            }
          }
        });
      }, 100);
    }
  }

  public search(name: string) {
    if (!this.customSearch) {
      this.loading = true;
      this.queryParams.search = name;
      this.queryParams.offset = 0;
      this.cdRef.markForCheck();

      this.basisCurrencyService.findWithPagination(this.queryParams).subscribe({
        next: (data) => {
          if (!data.items.some((item) => item.id === this._selectedCurrency?.id) && this._selectedCurrency) {
            this.currencies = data.items.concat(this._selectedCurrency);
          } else {
            this.currencies = data.items;
          }
          this.count = data.count;
          this.loading = false;
          this.cdRef.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.cdRef.markForCheck();
        }
      });
    }
  }

  public highlightMatchText(text: string): string {
    return HighlightMatchTextHelper.highlightMatchText(text, this.singleSelectComponent?.dropdown?.filterValue);
  }
}
